
import { useState, useRef, useEffect } from "react";
import { Video, X, Send, ArrowLeft, Circle, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cloudinaryService } from "@/services/cloudinaryService";

interface VideoResponseProps {
  postId: string;
  farmName: string;
  onClose: () => void;
  onSubmit: (response: { video: string; text: string }) => void;
}

export const VideoResponse = ({ postId, farmName, onClose, onSubmit }: VideoResponseProps) => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [responseText, setResponseText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Live video recording states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState<NodeJS.Timeout | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);

  // Cleanup function for camera stream
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (recordingTimer) {
        clearInterval(recordingTimer);
      }
    };
  }, [stream, recordingTimer]);

  const startLiveRecording = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'environment' // Prefer back camera for farming videos
        }, 
        audio: true 
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'video/webm;codecs=vp9' // Good quality for farming videos
      });
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks(prev => [...prev, event.data]);
        }
      };
      
      recorder.onstop = () => {
        handleRecordingComplete();
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Please allow camera access to record videos');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
      
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
    }
  };

  const handleRecordingComplete = async () => {
    if (recordedChunks.length === 0) return;
    
    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    setRecordedChunks([]);
    
    // Convert to File for upload
    const file = new File([blob], `recording-${Date.now()}.webm`, { type: 'video/webm' });
    
    // Upload the recorded video
    await uploadVideoFile(file);
  };

  const uploadVideoFile = async (file: File) => {
    // Validate file size (100MB limit)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      alert('Video file must be smaller than 100MB');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      const result = await cloudinaryService.uploadVideo(file, {
        userId: 'anonymous', // For anonymous uploads
        contentType: 'video',
        postTitle: 'video-response'
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setSelectedVideo(result.secure_url);
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);

    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload video. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = () => {
    if (selectedVideo && responseText.trim()) {
      onSubmit({
        video: selectedVideo,
        text: responseText
      });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between rounded-t-xl">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h3 className="text-lg font-semibold">Respond to {farmName}</h3>
          <Button
            onClick={handleSubmit}
            disabled={!selectedVideo || !responseText.trim() || isUploading}
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Send size={16} className="mr-1" />
            Send
          </Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Live Video Recording */}
          {!selectedVideo && !isUploading ? (
            <div className="space-y-4">
              {/* Camera Preview */}
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="w-full h-48 object-cover rounded-xl bg-muted"
                />
                
                {/* Recording Controls Overlay */}
                <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex items-center space-x-3">
                  {!isRecording ? (
                    <Button
                      onClick={startLiveRecording}
                      className="bg-red-600 hover:bg-red-700 text-white rounded-full p-4"
                    >
                      <Circle className="fill-current" size={24} />
                    </Button>
                  ) : (
                    <div className="flex items-center space-x-3">
                      <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        {formatRecordingTime(recordingTime)}
                      </div>
                      <Button
                        onClick={stopRecording}
                        className="bg-gray-600 hover:bg-gray-700 text-white rounded-full p-4"
                      >
                        <Square size={24} />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Recording indicator */}
                {isRecording && (
                  <div className="absolute top-3 left-3 flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                    <span className="text-white text-sm bg-black bg-opacity-50 px-2 py-1 rounded">
                      Recording
                    </span>
                  </div>
                )}
              </div>
              
              {/* Instructions */}
              <div className="text-center space-y-2">
                <h4 className="font-medium text-gray-700">Record Your Video Response</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>📱 Hold your device horizontally for better quality</p>
                  <p>🌾 Share your farming experience and advice</p>
                  <p>🎥 Tap the red button to start recording</p>
                </div>
              </div>
            </div>
          ) : isUploading ? (
            <div className="text-center space-y-4">
              <div className="flex flex-col items-center space-x-4">
                <Video className="text-purple-600 animate-pulse mb-2" size={32} />
                <div className="w-full max-w-xs">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Processing video... {uploadProgress}%</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <video
                src={selectedVideo}
                className="w-full h-48 object-cover rounded-xl"
                controls
                muted
              />
              <button
                onClick={() => setSelectedVideo(null)}
                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-1 hover:bg-opacity-70 w-6 h-6 flex items-center justify-center"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Response Text */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Add your advice or feedback
            </label>
            <Textarea
              placeholder="Share your experience, tips, or suggestions..."
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              className="resize-none"
              rows={3}
              disabled={isUploading}
            />
          </div>

          {/* Helper Text */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-800">
              💡 <strong>Tip:</strong> Share your experience, practical advice, or ask clarifying questions to help this farmer succeed!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
